import React, {
  useCallback,
  useEffect,
  useState,
  Suspense,
  useMemo,
} from 'react';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { useIdleTimer } from 'react-idle-timer';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'font-awesome/css/font-awesome.min.css';
import './App.css';
import './AppFont.css';
import 'assets/css/op2mise-color-palettes.css';
import AuthService from './core/auth.service';
import StringService from './core/string.service';
import ViewsComponent from './views/views';
import EventType from './enums/EventTypeEnum';
import ViewsService from './views/views.service';
import packageJson from '../package.json';
global.appVersion = packageJson.version;
import useStore, { getAccountUser } from 'store/AccountStore';
import RouterService from 'core/router.service';
import { Modal } from 'react-bootstrap';
import Button from 'shared/components/button/Button';
import TermsAcceptanceModal from 'shared/components/terms-acceptance-modal/TermsAcceptanceModal';
import useTermsAcceptance from 'shared/components/terms-acceptance-modal/useTermsAcceptance';
import ProfileService from './shared/profile/profile.service';
import { isEmptyObject } from 'utils';
import { lazyWithRetry } from 'utils/lazyWithRetry';
import useVersionCheck from 'hooks/useVersionCheck';

const LoginComponent = lazyWithRetry(
  () => import('./components/pages/login/LoginComponent'),
);
const ForgotPasswordComponent = lazyWithRetry(
  () => import('./components/forgot-password/ForgotPasswordComponent'),
);
const PasswordResetComponent = lazyWithRetry(
  () => import('./components/password-reset/PasswordResetComponent'),
);
const InternalServerComponent = lazyWithRetry(
  () =>
    import('./components/_error-pages/internal-server/InternalServerComponent'),
);
const AccessDeniedComponent = lazyWithRetry(
  () => import('./components/_error-pages/access-denied/AccessDeniedComponent'),
);
const NotFoundComponent = lazyWithRetry(
  () => import('./components/_error-pages/not-found/NotFoundComponent'),
);
const MaintenanceComponent = lazyWithRetry(
  () => import('./components/maintenance/MaintenanceComponent'),
);

function App() {
  const navigate = useNavigate();
  const {
    user,
    setUserData,
    isUserAuthenticated,
    setIsUserAuthenticated,
  } = useStore((state) => state);
  const userIdentity = AuthService.getUserIdentity();
  const [toNavigate, setToNavigate] = useState('');
  const [wasAutoLoggedOut, setWasAutoLoggedOut] = useState(false);

  const checkVersion = useVersionCheck();

  // Show when termsAccepted is false or undefined (missing from API); wait until user is hydrated
  const needsTermsAcceptance =
    isUserAuthenticated && !isEmptyObject(user) && user.termsAccepted !== true;

  const {
    termsContent,
    isLoadingTerms,
    isAccepting,
    errorMessage: termsErrorMessage,
    acceptTerms,
  } = useTermsAcceptance({ enabled: needsTermsAcceptance });

  // Done once on mount, not inside login(), so the login request already has the location
  // and signing in never waits for ipapi.co to answer.
  useEffect(() => {
    AuthService.resolveClientLocation();
  }, []);

  useEffect(() => {
    window.addEventListener('storage', (event) => {
      const credentials = JSON.parse(
        window.sessionStorage.getItem('userIdentity'),
      );

      if (event.key === EventType.Request_Credentials && credentials) {
        window.localStorage.setItem(
          EventType.Share_Credentials,
          JSON.stringify(credentials),
        );
        window.localStorage.removeItem(EventType.Share_Credentials);
      }
      if (event.key === EventType.Share_Credentials && !credentials) {
        window.sessionStorage.setItem('userIdentity', event.newValue);
        setIsUserAuthenticated(true);
      }
      if (event.key === EventType.Flush_Credentials && credentials) {
        window.sessionStorage.removeItem('userIdentity');
        setIsUserAuthenticated(false);
      }
    });

    window.localStorage.setItem(
      EventType.Request_Credentials,
      Date.now().toString(),
    );
    window.localStorage.removeItem(EventType.Request_Credentials);

    setIsUserAuthenticated(AuthService.isAuthenticated());
  }, []);

  const navigateToDefaultLanding = () => {
    setToNavigate(
      `${process.env.REACT_APP_SUBFOLDER}${RouterService.getDefaultLandingPath()}`,
    );
  };

  const handleGetUserLastAccessPage = async (userId) => {
    try {
      const response = await ViewsService.getUserLastAccess(userId);
      if (response?.data) {
        const lastAccessPage =
          response.data == '/scheduler'
            ? process.env.REACT_APP_SUBFOLDER + '/schedule'
            : process.env.REACT_APP_SUBFOLDER + response.data;
        setToNavigate(lastAccessPage);
      } else {
        navigateToDefaultLanding();
      }
    } catch (error) {}
  };

  const handleGetUserState = async () => {
    if (
      userIdentity &&
      (isEmptyObject(user) || userIdentity.userId != user.userId)
    ) {
      const response = await ProfileService.getUser(userIdentity.userId);
      setUserData(response.data);
    }
  };

  useEffect(() => {
    handleGetUserState();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdentity]);

  const handleOnLogin = useCallback(async (userId) => {
    setIsUserAuthenticated(true);
    const currentUser = getAccountUser();
    setToNavigate(
      `${process.env.REACT_APP_SUBFOLDER}${RouterService.getDefaultLandingPath({
        userPageAccess: currentUser?.userPageAccess,
        roleId: currentUser?.roleId,
        schedulesMenuType: currentUser?.schedulesMenuType,
      })}`,
    );
  }, [setIsUserAuthenticated]);

  const callbackUserLogout = () => {
    AuthService.logout();
    setIsUserAuthenticated(false);
  };

  const navigateToLogin = () => {
    navigate(`${process.env.REACT_APP_SUBFOLDER}/login`);
  };

  const goBackToSignin = () => {
    navigateToLogin();
    setWasAutoLoggedOut(false);
  };

  const onIdle = () => {
    if (isUserAuthenticated) {
      callbackUserLogout();
      navigateToLogin();
      setWasAutoLoggedOut(true);
    }
  };

  useIdleTimer({
    onIdle,
    timeout: 1740000,
    throttle: 500,
    crossTab: true,
    leaderElection: true,
    syncTimers: 200,
  });

  const sessionTimeoutNotificationModal = useMemo(() => {
    return (
      <Modal show={wasAutoLoggedOut} centered>
        <Modal.Body className="text-center p-4">
          <p
            style={{
              fontSize: '16px',
              marginTop: '20px',
              marginBottom: '30px',
            }}
          >
            You were automatically logged out due to inactivity.
          </p>

          <Button
            text={'Sign in'}
            onClick={goBackToSignin}
            style={{ marginBottom: '5px' }}
          />
        </Modal.Body>
      </Modal>
    );
  }, [wasAutoLoggedOut, goBackToSignin]);

  return (
    <div className="App">
      {sessionTimeoutNotificationModal}
      <TermsAcceptanceModal
        show={needsTermsAcceptance}
        onConfirm={acceptTerms}
        isLoading={isAccepting}
        isLoadingContent={isLoadingTerms}
        errorMessage={termsErrorMessage}
        termsContent={termsContent}
      />
      {isUserAuthenticated ? (
        <>
          <ViewsComponent
            toNavigate={toNavigate}
            callbackUserLogout={callbackUserLogout}
            callBackGetVersion={checkVersion}
            isUserAuthenticated={isUserAuthenticated}
          />
        </>
      ) : (
        <Routes>
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/login`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <LoginComponent onLogin={handleOnLogin} />
              </Suspense>
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/internal-server`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <InternalServerComponent />
              </Suspense>
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/access-denied`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <AccessDeniedComponent />
              </Suspense>
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/not-found`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <NotFoundComponent />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <Navigate to={`${process.env.REACT_APP_SUBFOLDER}/login`} />
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/forgot-password`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <ForgotPasswordComponent />
              </Suspense>
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/passwordreset/:urlParamId`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <PasswordResetComponent />
              </Suspense>
            }
          />
          <Route
            path={`${process.env.REACT_APP_SUBFOLDER}/maintenance`}
            element={
              <Suspense fallback={<div>Loading</div>}>
                <MaintenanceComponent />
              </Suspense>
            }
          />
        </Routes>
      )}
    </div>
  );
}

export default App;
